// AuthGate
// ========
//
// Wave 14bs: Blockiert die App-UI bis der User eingeloggt ist.
// Wave 14bx: AuthGate umschliesst jetzt den FirstLaunchGate (nicht umgekehrt
// wie vorher). Heisst: Login-Screen kommt ZUERST, erst nach erfolgreichem
// Login startet der Offline-Map-Download. Für Early-Access wollen wir keine
// Bandbreite vor Auth verbraten.
//
// UI-Zustände:
//   - 'checking'          : /api/me-Abfrage läuft
//   - 'signed-out:login'  : Login-Form (Email → Magic-Link)
//   - 'signed-out:signup' : Signup-Form (Email + Name + Invite + Privacy-Checkbox)
//   - 'link-sent'         : Bestätigung, User prüft Inbox
//   - 'privacy'           : Privacy-Statement (Volltext, scrollbar)
//   - 'verifying'         : Magic-Link wurde geöffnet, /verify läuft
//   - 'signed-in'         : Children rendern
//
// Magic-Link-Token kommt via URL: /auth/verify?token=... — wir fangen das
// beim App-Start ab, verifizieren, und cleanen die URL (history.replaceState).

import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// Wave 14bu-fix3: Build-Version-Marker auf der Login-Screen. Gleiche Konvention
// wie bei der Kompass-Rose (deploy-staging.sh / deploy-prod.sh setzen die
// Env-Var). Wenn der Border ums Email-Feld die erwartete Deploy-Farbe hat,
// weiss der User dass die PWA die frische Version geladen hat.
const BUILD_MARKER_COLOR =
  (import.meta.env.VITE_BUILD_MARKER_COLOR as string | undefined) || '#ff00ff';

// Waitlist-Endpoint: Google-Apps-Script-Web-App, die an die Tabelle
// "ULTRA Interessenten" eine Zeile [Zeitstempel, Vorname, Email] anhängt.
// POST als text/plain + mode:'no-cors' (simple request → kein CORS-Preflight,
// Apps-Script-Web-Apps setzen keine CORS-Header). Antwort ist deshalb opaque;
// wir werten optimistisch als Erfolg, solange kein Netzwerkfehler fliegt.
const WAITLIST_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbwgv7YLw-_zsTJc9GvHo8OYWYmsipA3A0tNDZXpW7yMvyvAaPgbMP1iW51bVdhcQFr-/exec';
import {
  fetchMe,
  fetchSession,
  getCachedUser,
  submitSignup,
  submitLogin,
  verifyToken,
  verifyCode,
  checkEarlyAccessCode,
  logout,
  type SessionUser,
  type VerifyResult,
} from '../lib/auth';
import { installHeartbeat } from '../lib/heartbeat';
import { PrivacyStatement } from './PrivacyStatement';

type View =
  | { kind: 'checking' }
  | { kind: 'landing' }
  | { kind: 'link-sent'; email: string; purpose: 'login' | 'signup' }
  | { kind: 'privacy' }
  | { kind: 'waitlist' }
  | { kind: 'verifying' }
  | {
      kind: 'verify-error';
      error: string;
      /**
       * Bei error='device_cap_reached' listet der Server die aktuell
       * aktiven Geräte — UI zeigt sie an damit der User weiss auf welchem
       * er sich ausloggen soll.
       */
      devices?: Array<{
        id: string;
        device_name: string;
        last_seen_at: string;
        created_at: string;
      }>;
      cap?: number;
      /**
       * Wave 14ca-fix34: Wenn der Cap-Fehler aus dem Code-Login-Pfad kommt
       * (LinkSentView mit 6-stelligem Code), reichen wir Email+Code durch
       * damit der "Sign out all"-Button den Code mit replaceAll:true noch-
       * mal verwenden kann (statt nach einem URL-Token zu suchen das es
       * im PWA-Pfad nie gegeben hat).
       */
      retryEmail?: string;
      retryCode?: string;
    }
  | { kind: 'signed-in'; user: SessionUser };

export function AuthGate({ children }: { children: ReactNode }) {
  const [view, setView] = useState<View>({ kind: 'checking' });
  const navigate = useNavigate();

  // Startup: URL auf Verify-Token prüfen, sonst /api/me fetchen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      const path = window.location.pathname;
      const isVerifyPath = path === '/auth/verify' || path === '/auth/verify/';

      if (isVerifyPath && urlToken) {
        setView({ kind: 'verifying' });
        const res = await verifyToken(urlToken);
        if (cancelled) return;
        if (res.ok && res.user) {
          // Wave 14bx-fix: `window.history.replaceState` ändert NUR die
          // Adressleiste — React Router liest aber seine Location aus dem
          // internen BrowserHistory-Store. Ohne ein `popstate`-Event weiss
          // er nichts vom URL-Change, bleibt weiter auf `/auth/verify` und
          // die Routes matchen den wildcard → NotFoundPage. Deshalb
          // `navigate('/', {replace:true})` — das durchläuft React Router
          // sauber und spiegelt sich in der Adressleiste.
          navigate('/', { replace: true });
          setView({ kind: 'signed-in', user: res.user });
        } else {
          setView({
            kind: 'verify-error',
            error: res.error || 'unknown',
            devices: res.devices,
            cap: res.cap,
          });
        }
        return;
      }

      // Session-Check beim Start. Nur ein echtes 401/403 fuehrt zum
      // Login-Screen — ist der Server nicht erreichbar (Kaltstart ohne Netz,
      // Deploy, Funkloch), gilt die zuletzt bekannte Session weiter. Sonst
      // steht der User bei jedem App-Start vor dem Login, obwohl seine
      // Session 30 Tage laeuft (Bug vom 2026-08-26).
      const res = await fetchSession();
      if (cancelled) return;
      if (res.status === 'signed-in') {
        setView({ kind: 'signed-in', user: res.user });
      } else if (res.status === 'signed-out') {
        setView({ kind: 'landing' });
      } else {
        const cached = getCachedUser();
        setView(cached ? { kind: 'signed-in', user: cached } : { kind: 'landing' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Nachziehen, sobald wieder Netz da ist: war der Start offline und wir
  // laufen auf der gecachten Session, holen wir die Bestaetigung nach. Ein
  // echtes 401 meldet dann ab — aber eben erst, wenn der Server das sagt.
  useEffect(() => {
    if (view.kind !== 'signed-in') return;
    const recheck = async () => {
      const res = await fetchSession();
      if (res.status === 'signed-in') setView({ kind: 'signed-in', user: res.user });
      else if (res.status === 'signed-out') setView({ kind: 'landing' });
      // 'unreachable' → alles beim Alten lassen
    };
    window.addEventListener('online', recheck);
    return () => window.removeEventListener('online', recheck);
  }, [view.kind]);

  if (view.kind === 'signed-in') {
    return <SignedInShell>{children}</SignedInShell>;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-turbo-900 p-4 z-50">
      <div className="w-full max-w-sm text-white">
        {view.kind === 'checking' && <CenteredSpinner label="Checking session…" />}
        {view.kind === 'verifying' && <CenteredSpinner label="Signing you in…" />}
        {view.kind === 'verify-error' && (
          <VerifyError
            reason={view.error}
            devices={view.devices}
            cap={view.cap}
            retryEmail={view.retryEmail}
            retryCode={view.retryCode}
            onBack={() => setView({ kind: 'landing' })}
          />
        )}
        {view.kind === 'landing' && (
          <LandingView
            onLinkSent={(email, purpose) =>
              setView({ kind: 'link-sent', email, purpose })
            }
            onQuickAccess={(user) => setView({ kind: 'signed-in', user })}
            onShowPrivacy={() => setView({ kind: 'privacy' })}
            onShowWaitlist={() => setView({ kind: 'waitlist' })}
          />
        )}
        {view.kind === 'link-sent' && (
          <LinkSentView
            email={view.email}
            purpose={view.purpose}
            onBack={() => setView({ kind: 'landing' })}
            onVerified={(user) => setView({ kind: 'signed-in', user })}
            onVerifyError={(err) =>
              setView({
                kind: 'verify-error',
                error: err.error || 'unknown',
                devices: err.devices,
                cap: err.cap,
                retryEmail: err.retryEmail,
                retryCode: err.retryCode,
              })
            }
          />
        )}
        {view.kind === 'privacy' && (
          <PrivacyView onBack={() => setView({ kind: 'landing' })} />
        )}
        {view.kind === 'waitlist' && (
          <WaitlistView onBack={() => setView({ kind: 'landing' })} />
        )}
      </div>
    </div>
  );
}

/**
 * Wrapper, der beim Mount den Heartbeat startet. Sitzt zwischen AuthGate und
 * dem gerenderten App-Tree, sodass wir sicher sind, dass Heartbeat nur bei
 * eingeloggtem User läuft.
 */
function SignedInShell({ children }: { children: ReactNode }) {
  useEffect(() => {
    installHeartbeat();
  }, []);
  return <>{children}</>;
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12">
      <div className="w-6 h-6 rounded-full border-2 border-orange-500 border-r-transparent animate-spin" />
      <p className="text-white/60 text-sm">{label}</p>
    </div>
  );
}

function Header({ subtitle }: { subtitle: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <img
        src="/icons/icon-192.png?v=14bs1"
        alt="All Day Turbo ULTRA"
        width={40}
        height={40}
        className="w-10 h-10 rounded-lg shrink-0"
      />
      <div>
        <h1 className="text-lg font-semibold leading-tight">All Day Turbo ULTRA</h1>
        <p className="text-white/55 text-xs">{subtitle}</p>
      </div>
    </div>
  );
}

/**
 * Wave 14bz — LandingView: EAC-gated Signup + Email-Login in einem Screen.
 *
 *   1. Grosses Early-Access-Code-Feld oben (primary CTA). Submit → POST
 *      /api/auth/check-eac (kein Account-Creation). Valide → Felder
 *      Name+Email+Privacy expandieren inline.
 *   2. Trenner "or".
 *   3. Kleinerer Email-Login-Block für bestehende Mitglieder.
 */
function LandingView({
  onLinkSent,
  onQuickAccess,
  onShowPrivacy,
  onShowWaitlist,
}: {
  onLinkSent: (email: string, purpose: 'login' | 'signup') => void;
  onQuickAccess: (user: SessionUser) => void;
  onShowPrivacy: () => void;
  onShowWaitlist: () => void;
}) {
  // --- EAC-Block ---
  const [eacInput, setEacInput] = useState('');
  const [eacValidated, setEacValidated] = useState<string | null>(null);
  const [eacChecking, setEacChecking] = useState(false);
  const [eacError, setEacError] = useState<string | null>(null);

  // --- Signup-Fields (nur sichtbar wenn eacValidated !== null) ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [signupPending, setSignupPending] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  // --- Login-Block ---
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const checkCode = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // Wave 14cb: Backdoor-Code wird CASE-SENSITIVE verglichen
      // (constant-time vs. ADT_BACKDOOR_PASSWORD im Backend). Wir
      // schicken den Original-Input UNGEÄNDERT mit, damit "pusio" ≠ "PUSIO"
      // sauber funktioniert. Der Server normalized erst NACH der Backdoor-
      // Prüfung auf upper-case (für die normalen EAC/Invite-Lookups).
      const raw = eacInput.trim();
      if (!raw || eacChecking) return;
      setEacChecking(true);
      setEacError(null);
      const res = await checkEarlyAccessCode(raw);
      setEacChecking(false);
      if (res.backdoor && res.user) {
        // Wave 14cb: Server hat den Backdoor-Code akzeptiert UND uns
        // gleich eingeloggt. Direkt in den eingeloggten State springen,
        // analog zum Quick-Access-Login-Pfad. Token wurde in
        // checkEarlyAccessCode bereits in localStorage gespiegelt.
        onQuickAccess(res.user);
        return;
      }
      if (res.valid) {
        // Normaler EAC-Flow → Signup-Felder einblenden. Hier brauchen wir
        // den UPPER-CASED Code, weil der Server bei /signup gegen die
        // upper-case-Tabelle vergleicht.
        setEacValidated(raw.toUpperCase());
      } else if (res.used) {
        setEacError(
          'This code has already been used. If that was you, sign in with your ' +
            'email below — invite codes only work once.',
        );
      } else {
        setEacError("That code isn't valid. Check with whoever invited you.");
      }
    },
    [eacInput, eacChecking, onQuickAccess],
  );

  const submitSignupForm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (signupPending || !eacValidated) return;
      if (!acceptedPrivacy) {
        setSignupError('Please accept the privacy statement to continue.');
        return;
      }
      setSignupPending(true);
      setSignupError(null);
      const res = await submitSignup({
        email: signupEmail.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        inviteCode: eacValidated,
        acceptedPrivacy: true,
      });
      setSignupPending(false);
      if (!res.ok) {
        setSignupError(formatAuthError(res.error));
        return;
      }
      onLinkSent(signupEmail.trim(), 'signup');
    },
    [
      eacValidated,
      signupEmail,
      firstName,
      lastName,
      acceptedPrivacy,
      signupPending,
      onLinkSent,
    ],
  );

  const submitLoginForm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!loginEmail.trim() || loginPending) return;
      setLoginPending(true);
      setLoginError(null);
      const res = await submitLogin(loginEmail.trim());
      setLoginPending(false);
      if (!res.ok) {
        setLoginError(formatAuthError(res.error, res.detail));
        return;
      }
      if (res.quickAccess && res.user) {
        onQuickAccess(res.user);
        return;
      }
      if (res.needsSignup) {
        setLoginError(
          "We can't find that email. Use your early-access code above to sign up.",
        );
        return;
      }
      onLinkSent(loginEmail.trim(), 'login');
    },
    [loginEmail, loginPending, onLinkSent, onQuickAccess],
  );

  return (
    <div className="space-y-5">
      <Header subtitle="Early access" />

      {/* --- BLOCK 1: EAC-Gated Signup --- */}
      <section className="rounded-xl bg-turbo-700/20 border border-orange-500/20 p-4 space-y-3">
        <h2 className="text-base font-semibold leading-tight">
          Got an early-access code?
        </h2>
        <p className="text-xs text-white/55 -mt-1">
          Enter it below to create your account.
        </p>

        {eacValidated === null ? (
          <form onSubmit={checkCode} className="space-y-3">
            <input
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              required
              value={eacInput}
              onChange={(e) => setEacInput(e.target.value.toUpperCase())}
              className="w-full rounded-lg bg-turbo-900/70 px-4 py-3.5 text-[22px] tracking-[0.2em] font-mono text-center outline-none focus:ring-2 focus:ring-orange-500 placeholder:text-white/25"
              style={{ border: `2px solid ${BUILD_MARKER_COLOR}` }}
              placeholder="CODE"
              maxLength={32}
            />
            {eacError && <p className="text-red-300 text-sm">{eacError}</p>}
            <button
              type="submit"
              disabled={eacChecking || !eacInput.trim()}
              className="w-full py-3 rounded-lg bg-orange-500 text-white font-semibold text-base disabled:opacity-60"
            >
              {eacChecking ? 'Checking…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitSignupForm} className="space-y-3">
            <div className="rounded-lg bg-emerald-500/15 border border-emerald-400/30 px-3 py-2 flex items-center gap-2 text-sm">
              <span className="text-emerald-300">✓</span>
              <span className="font-mono text-emerald-100 tracking-wider">
                {eacValidated}
              </span>
              <span className="text-emerald-200/70 text-xs flex-1">
                Code accepted
              </span>
              <button
                type="button"
                onClick={() => {
                  setEacValidated(null);
                  setEacInput('');
                  setSignupError(null);
                }}
                className="text-xs text-emerald-200/80 underline"
              >
                change
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-white/60 mb-1">First name</span>
                <input
                  type="text"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-lg bg-turbo-900/70 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-white/60 mb-1">Last name</span>
                <input
                  type="text"
                  autoComplete="family-name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-lg bg-turbo-900/70 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs text-white/60 mb-1">Email</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="w-full rounded-lg bg-turbo-900/70 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
                placeholder="you@example.com"
              />
            </label>

            <label className="flex items-start gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-1 shrink-0"
                required
              />
              <span>
                I have read and accept the{' '}
                <button
                  type="button"
                  onClick={onShowPrivacy}
                  className="underline text-orange-400"
                >
                  privacy statement
                </button>
                .
              </span>
            </label>

            {signupError && <p className="text-red-300 text-sm">{signupError}</p>}

            <button
              type="submit"
              disabled={signupPending}
              className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold disabled:opacity-60"
            >
              {signupPending ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}
      </section>

      {/* --- Trenner --- */}
      <div className="flex items-center gap-3" aria-hidden>
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[11px] text-white/40 uppercase tracking-wider">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* --- BLOCK 2: Email-Login für bestehende Mitglieder --- */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-white/90">Already a member?</h2>
        <form onSubmit={submitLoginForm} className="space-y-2">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full rounded-lg bg-turbo-700/40 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
            placeholder="your@email.com"
          />
          {loginError && <p className="text-red-300 text-sm">{loginError}</p>}
          <button
            type="submit"
            disabled={loginPending || !loginEmail.trim()}
            className="w-full py-2 rounded-lg bg-turbo-700/60 hover:bg-turbo-700/80 text-white/95 text-sm font-semibold disabled:opacity-50"
          >
            {loginPending ? 'Sending link…' : 'Send sign-in link'}
          </button>
        </form>
      </section>

      {/* --- BLOCK 3: Waitlist für Interessierte ohne Early-Access-Code --- */}
      <div className="pt-1 text-center">
        <p className="text-xs text-white/50 mb-1.5">No early-access code yet?</p>
        <button
          type="button"
          onClick={onShowWaitlist}
          className="text-sm font-semibold text-orange-400 underline underline-offset-2"
        >
          Join the waiting list →
        </button>
      </div>
    </div>
  );
}

function LinkSentView({
  email,
  purpose,
  onBack,
  onVerified,
  onVerifyError,
}: {
  email: string;
  purpose: 'login' | 'signup';
  onBack: () => void;
  onVerified: (user: SessionUser) => void;
  onVerifyError: (err: {
    error?: string;
    devices?: VerifyResult['devices'];
    cap?: number;
    retryEmail?: string;
    retryCode?: string;
  }) => void;
}) {
  // Wave 14ca-fix33: Code-Input für PWA-User. iOS Mail öffnet den Link in
  // Safari, nicht in der PWA — daher der 6-stellige Code als primärer
  // Login-Pfad in der App.
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || !/^\d{6}$/.test(code)) return;
    setPending(true);
    setError(null);
    const res = await verifyCode(email, code);
    setPending(false);
    if (res.ok && res.user) {
      onVerified(res.user);
    } else if (res.error === 'device_cap_reached') {
      onVerifyError({
        error: res.error,
        devices: res.devices,
        cap: res.cap,
        // Wave 14ca-fix34: Email+Code mitgeben damit der "Sign out all"-
        // Button im DeviceCapReachedView den gleichen Code mit
        // replaceAll:true wiederverwendet. Ohne diese Info würde er nach
        // einem URL-Token suchen das es im PWA-Code-Login-Pfad nicht gibt.
        retryEmail: email,
        retryCode: code,
      });
    } else {
      const map: Record<string, string> = {
        invalid_code: "That code doesn't match. Check your email and try again.",
        code_expired: 'That code has expired. Request a new one.',
        code_already_used: 'That code was already used. Request a new one.',
        invalid_email: 'Email looks invalid. Go back and try again.',
        network: 'Network error. Try again in a moment.',
      };
      setError(map[res.error || ''] || `Verification failed: ${res.error}`);
    }
  };

  return (
    <div className="space-y-4">
      <Header subtitle="Check your email" />

      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-orange-500/20 mx-auto flex items-center justify-center mb-3">
          <span className="text-2xl">✉</span>
        </div>
        <p className="text-sm text-white/80">
          We sent a 6-digit code to{' '}
          <strong className="break-all">{email}</strong>.
        </p>
      </div>

      {/* Code-Input — Primary Path für PWA */}
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-xs text-white/65 mb-1.5 text-center">
            Enter the 6-digit code from your email
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(digits);
              setError(null);
            }}
            placeholder="000000"
            className="w-full rounded-lg bg-turbo-900/70 px-4 py-3.5 text-[26px] tracking-[0.4em] font-mono text-center outline-none focus:ring-2 focus:ring-orange-500 placeholder:text-white/20 tabular-nums"
            autoFocus
          />
        </label>
        {error && (
          <p className="text-red-300 text-sm text-center">{error}</p>
        )}
        <button
          type="submit"
          disabled={pending || code.length !== 6}
          className="w-full py-3 rounded-lg bg-orange-500 text-white font-semibold text-base disabled:opacity-50"
        >
          {pending
            ? 'Verifying…'
            : purpose === 'signup'
              ? 'Create account'
              : 'Sign in'}
        </button>
      </form>

      <p className="text-[11px] text-white/45 text-center leading-relaxed">
        Or click the link in the email if you opened it on your computer. The
        code and link both expire in 15 minutes.
      </p>

      <button
        onClick={onBack}
        className="text-sm text-orange-400 underline block mx-auto"
      >
        Use a different email
      </button>
    </div>
  );
}

function PrivacyView({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-4">
      <Header subtitle="Privacy statement" />
      <div className="max-h-[65vh] overflow-y-auto rounded-lg bg-turbo-700/30 border border-turbo-700 p-4 text-sm text-white/85 leading-relaxed">
        <PrivacyStatement />
      </div>
      <button
        onClick={onBack}
        className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold"
      >
        Back to sign-up
      </button>
    </div>
  );
}

/**
 * Wave 14cg — WaitlistView: für Interessierte OHNE Early-Access-Code. Sie
 * hinterlassen Vorname + Email; der Datensatz geht per POST an eine Google-
 * Apps-Script-Web-App (WAITLIST_ENDPOINT), die eine Zeile an die Tabelle
 * "ULTRA Interessenten" anhängt. Kein Backend, kein Server-Secret. Genutzt
 * wird die Adresse laut Datenschutz ausschliesslich für EINE einmalige
 * Launch-Benachrichtigung.
 */
function WaitlistView({ onBack }: { onBack: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    const fn = firstName.trim();
    const em = email.trim();
    if (!fn) {
      setError('Please enter your first name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError('Please enter a valid email address.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      // Apps-Script-Web-App: simple request (text/plain, no-cors) → kein
      // CORS-Preflight. Antwort ist opaque, daher optimistisch als Erfolg,
      // solange kein Netzwerkfehler fliegt.
      await fetch(WAITLIST_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ firstName: fn, email: em }),
      });
      setDone(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setPending(false);
    }
  };

  if (showPrivacy) {
    return (
      <div className="space-y-4">
        <Header subtitle="Waiting list — privacy" />
        <div className="max-h-[65vh] overflow-y-auto rounded-lg bg-turbo-700/30 border border-turbo-700 p-4 text-sm text-white/85 leading-relaxed">
          <WaitlistPrivacyStatement />
        </div>
        <button
          onClick={() => setShowPrivacy(false)}
          className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold"
        >
          Back to the waiting list
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="space-y-4 text-center">
        <Header subtitle="Waiting list" />
        <div className="w-14 h-14 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center">
          <span className="text-2xl text-emerald-300">✓</span>
        </div>
        <h2 className="text-base font-semibold">You're on the list!</h2>
        <p className="text-sm text-white/70">
          Thanks{firstName.trim() ? `, ${firstName.trim()}` : ''}. We'll email
          you once — and only once — when All Day Turbo ULTRA opens to the
          public.
        </p>
        <button
          onClick={onBack}
          className="w-full py-2.5 rounded-lg bg-turbo-700/60 text-white/90 text-sm font-semibold"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Header subtitle="Waiting list" />
      <p className="text-sm text-white/75 leading-relaxed">
        All Day Turbo ULTRA is in early access. Leave your name and email and
        we&apos;ll notify you the moment it&apos;s open to everyone.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-xs text-white/60 mb-1">First name</span>
          <input
            type="text"
            autoComplete="given-name"
            required
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setError(null);
            }}
            className="w-full rounded-lg bg-turbo-900/70 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
          />
        </label>
        <label className="block">
          <span className="block text-xs text-white/60 mb-1">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            className="w-full rounded-lg bg-turbo-900/70 border border-turbo-700 px-3 py-2 text-[16px] outline-none focus:border-orange-500"
            placeholder="you@example.com"
          />
        </label>

        <p className="text-[11px] text-white/50 leading-snug">
          We use your name and email{' '}
          <strong className="text-white/70">only</strong> to send you a single
          notification email when the app becomes publicly available — nothing
          else, no marketing, no sharing. See the{' '}
          <button
            type="button"
            onClick={() => setShowPrivacy(true)}
            className="underline text-orange-400"
          >
            privacy statement
          </button>
          .
        </p>

        {error && <p className="text-red-300 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={pending}
          className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold disabled:opacity-60"
        >
          {pending ? 'Adding you…' : 'Notify me at launch'}
        </button>
      </form>

      <button
        onClick={onBack}
        className="text-sm text-orange-400 underline block mx-auto"
      >
        Back to sign in
      </button>
    </div>
  );
}

/**
 * Datenschutz für die Waitlist. Bewusst knapp: die Daten werden ausschliesslich
 * für EINE einmalige Launch-Benachrichtigung genutzt, für nichts anderes.
 */
function WaitlistPrivacyStatement() {
  return (
    <div className="space-y-3">
      <section>
        <h3 className="font-semibold text-white mb-1">What this list is for</h3>
        <p>
          The All Day Turbo ULTRA waiting list lets you be notified when the app,
          currently in early access, becomes available to the public.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-white mb-1">What we store</h3>
        <p>Only the first name and email address you enter here. Nothing else.</p>
      </section>

      <section>
        <h3 className="font-semibold text-white mb-1">How we use it</h3>
        <p>
          Your data is used <strong>exclusively</strong> to send you a{' '}
          <strong>single, one-time email</strong> when All Day Turbo ULTRA opens
          to the public. It is used for no other purpose whatsoever — no
          marketing, no newsletters, no profiling, no advertising, and it is
          never sold, rented, or shared with third parties.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-white mb-1">Your rights (GDPR)</h3>
        <p>
          You can ask us to access or delete your entry at any time (GDPR Art. 15
          and 17). Once the launch email has been sent, your entry is no longer
          needed and can be removed. To make a request, reply to that email.
        </p>
      </section>
    </div>
  );
}

function VerifyError({
  reason,
  devices,
  cap,
  retryEmail,
  retryCode,
  onBack,
}: {
  reason: string;
  devices?: Array<{
    id: string;
    device_name: string;
    last_seen_at: string;
    created_at: string;
  }>;
  cap?: number;
  retryEmail?: string;
  retryCode?: string;
  onBack: () => void;
}) {
  // Wave 14bz — Device-Cap-Spezialfall: eigene Ansicht mit Geräte-Liste,
  // damit der User weiss welche er ausloggen muss.
  if (reason === 'device_cap_reached') {
    return (
      <DeviceCapReachedView
        devices={devices}
        cap={cap}
        retryEmail={retryEmail}
        retryCode={retryCode}
        onBack={onBack}
      />
    );
  }

  let title = 'This link no longer works';
  let detail = 'Please request a new sign-in email.';
  if (reason === 'token_expired') {
    title = 'The link has expired';
    detail = 'Magic links are valid for 15 minutes. Request a new one below.';
  } else if (reason === 'token_already_used') {
    title = 'This link was already used';
    detail =
      'Each magic link can only be used once. Request a new one if you need to sign in again.';
  } else if (reason === 'invite_invalid' || reason === 'invite_already_used') {
    title = 'Early-access code not valid';
    detail =
      'Please double-check your early-access code — or ask the founder for a fresh one.';
  }
  return (
    <div className="space-y-4 text-center">
      <Header subtitle="Hmm." />
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-sm text-white/70">{detail}</p>
      <button
        onClick={onBack}
        className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold"
      >
        Back to sign in
      </button>
    </div>
  );
}

/**
 * Wave 14bz — Screen wenn der User den Device-Cap (2 aktive Sessions)
 * erreicht hat. Zeigt die aktuellen Devices + bietet einen Logout-Knopf
 * falls der User grade auf einem dieser Devices ist (dann kann er hier
 * direkt ausloggen ohne erst zur Profile-Page zu gehen).
 *
 * Die currentUser-Detection läuft via fetchMe() on mount: wenn das Gerät
 * einen aktiven Session-Cookie hat, ist hier die Logout-Option sinnvoll.
 * Sonst (fresh Gerät ohne Session) können wir nur auf die Anderen Geräte
 * verweisen.
 */
function DeviceCapReachedView({
  devices,
  cap,
  retryEmail,
  retryCode,
  onBack,
}: {
  devices?: Array<{
    id: string;
    device_name: string;
    last_seen_at: string;
    created_at: string;
  }>;
  cap?: number;
  /** Wave 14ca-fix34: bei Code-Login-Pfad (PWA) durchgereicht damit der
      "Sign out all"-Button verifyCode(email, code, {replaceAll: true})
      aufrufen kann statt nach einem URL-Token zu suchen das nicht da ist. */
  retryEmail?: string;
  retryCode?: string;
  onBack: () => void;
}) {
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [replacingAll, setReplacingAll] = useState(false);
  const [replaceError, setReplaceError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => {
      if (!cancelled) setCurrentUser(u);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signOutHere = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      // Wave 14bz-fix: cache-bust-URL statt reload, damit ein alter
      // Service-Worker nicht die gecachte signed-in-App-Shell liefert.
      window.location.href = `/?_logout=${Date.now()}`;
    }
  };

  // Wave 14bz-fix / 14ca-fix34: Escape-Hatch. Wenn der User in der Device-
  // Cap-Falle festhängt, kann er hier "alle anderen Devices abmelden und
  // hier einloggen" klicken.
  //
  // Zwei Pfade:
  //   1. Code-Login (PWA): retryEmail + retryCode wurden vom LinkSentView
  //      durchgereicht. Wir rufen verifyCode mit replaceAll:true auf.
  //   2. Klassischer Magic-Link (Browser): Token steckt in der URL.
  //      verifyToken mit replaceAll:true.
  // Wenn keiner der Pfade applicable ist (z.B. User landete hier durch
  // direkten /auth/verify-Open ohne Token), zeigen wir den Hint zum neuen
  // Login.
  const replaceAllAndSignIn = async () => {
    if (replacingAll) return;
    setReplacingAll(true);
    setReplaceError(null);
    try {
      // Pfad 1: Code-Login (PWA)
      if (retryEmail && retryCode) {
        const res = await verifyCode(retryEmail, retryCode, { replaceAll: true });
        if (res.ok && res.user) {
          window.location.href = '/';
          return;
        }
        setReplaceError(
          res.error === 'code_expired'
            ? 'Your code has expired. Request a new one from the start screen.'
            : res.error === 'code_already_used'
              ? 'That code was already used. Request a fresh one.'
              : `Couldn't replace sessions (${res.error || 'unknown'}). Try the other options.`,
        );
        return;
      }

      // Pfad 2: klassischer Magic-Link via URL-Token
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      if (!urlToken) {
        setReplaceError(
          "This page was opened without a sign-in code or token. Request a new sign-in email and come back.",
        );
        return;
      }
      const res = await verifyToken(urlToken, { replaceAll: true });
      if (res.ok && res.user) {
        window.location.href = '/';
        return;
      }
      setReplaceError(
        res.error === 'token_expired'
          ? 'Your magic link has expired. Request a new one from the start screen.'
          : res.error === 'token_already_used'
            ? 'This magic link was already used. Request a fresh one.'
            : `Couldn't replace sessions (${res.error || 'unknown'}). Try the other options.`,
      );
    } catch (err) {
      setReplaceError(String(err instanceof Error ? err.message : err));
    } finally {
      setReplacingAll(false);
    }
  };

  const deviceCount = devices?.length ?? cap ?? 2;

  return (
    <div className="space-y-4">
      <Header subtitle="Too many devices" />
      <h2 className="text-base font-semibold">
        You're already signed in on {deviceCount} devices
      </h2>
      <p className="text-sm text-white/70">
        To keep your account safe, All Day Turbo ULTRA allows up to {cap ?? 2}
        {' '}simultaneous devices. Sign out from one of the devices listed
        below, then tap the magic link in your email again.
      </p>

      {devices && devices.length > 0 && (
        <ul className="rounded-lg bg-turbo-700/40 border border-turbo-700 divide-y divide-turbo-700">
          {devices.map((d) => (
            <li key={d.id} className="px-3 py-2 text-sm">
              <div className="font-medium">{d.device_name || 'Unknown device'}</div>
              <div className="text-xs text-white/55 mt-0.5">
                Last active: {new Date(d.last_seen_at).toLocaleString()}
              </div>
            </li>
          ))}
        </ul>
      )}

      {currentUser && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-3 space-y-2">
          <div className="text-xs text-emerald-200/80 leading-snug">
            You're currently signed in on this device as{' '}
            <span className="font-medium text-emerald-100">
              {currentUser.email}
            </span>. You can sign out right here to free a slot, then reopen
            the magic-link email on the device you want to switch to.
          </div>
          <button
            type="button"
            onClick={signOutHere}
            disabled={loggingOut}
            className="w-full py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100 text-sm font-semibold disabled:opacity-50"
          >
            {loggingOut ? 'Signing out…' : 'Sign out on this device'}
          </button>
        </div>
      )}

      <p className="text-[11px] text-white/45 leading-snug">
        Alternative: on the other device, open the app, go to
        Profile → Sign out. Then tap the magic-link on THIS device —
        links stay valid for 15 minutes.
      </p>

      {/* Wave 14bz — Escape-Hatch: Nuclear-Option. Revoked serverseitig
          ALLE aktiven Sessions des Users und loggt hier ein. Nutzbar
          wenn der User aus irgendeinem Grund (SW-Cache, Cookie-Eviction,
          Logout auf Altgerät hat nichts revoked) festhängt. */}
      <div className="rounded-lg bg-rose-500/10 border border-rose-400/30 p-3 space-y-2">
        <div className="text-xs text-rose-200/80 leading-snug">
          <strong className="text-rose-100">Stuck?</strong> If you've tried
          signing out elsewhere but this screen keeps coming back, force
          a reset: this revokes every active session on your account and
          signs you in here.
        </div>
        <button
          type="button"
          onClick={replaceAllAndSignIn}
          disabled={replacingAll}
          className="w-full py-2 rounded-lg bg-rose-500/25 hover:bg-rose-500/35 border border-rose-400/40 text-rose-100 text-sm font-semibold disabled:opacity-50"
        >
          {replacingAll
            ? 'Resetting…'
            : 'Sign out all other devices and continue here'}
        </button>
        {replaceError && (
          <p className="text-rose-200 text-xs">{replaceError}</p>
        )}
      </div>

      <button
        onClick={onBack}
        className="w-full py-2.5 rounded-lg bg-turbo-700/60 text-white/90 text-sm font-semibold"
      >
        Back to sign in
      </button>
    </div>
  );
}

function formatAuthError(err?: string, detail?: string): string {
  switch (err) {
    case 'invalid_email':
      return 'That does not look like a valid email. Please check for typos.';
    case 'first_name_required':
      return 'Please enter your first name.';
    case 'last_name_required':
      return 'Please enter your last name.';
    case 'name_too_long':
      return 'Names must be under 60 characters.';
    case 'invite_required':
      return 'An early-access code is required.';
    case 'invite_invalid':
      return 'That early-access code is not valid.';
    case 'invite_already_used':
      return 'That early-access code has already been used.';
    case 'privacy_required':
      return 'Please accept the privacy statement to continue.';
    case 'network':
      return 'Network error. Please check your connection and try again.';
    // Wave 14bu-fix: Quick-Access-spezifische Fehler, damit wir beim Debuggen
    // sehen welcher Codepfad fehlschlägt. Wave 14bu-fix2: Wenn der Server ein
    // `detail` mitgeschickt hat, zeigen wir das konkret an — ohne das sind wir
    // blind und können die Ursache nicht finden.
    case 'quick_access_failed':
      return detail
        ? `Sign-in failed: ${detail}`
        : 'Sign-in failed during session setup. Try again in a moment.';
    case 'http_500':
    case 'Internal Server Error':
      return detail
        ? `Server error: ${detail}`
        : 'Server error. Please try again in a moment.';
    default:
      if (detail) {
        return err
          ? `Something went wrong (${err}: ${detail}). Please try again.`
          : `Something went wrong: ${detail}. Please try again.`;
      }
      return err
        ? `Something went wrong (${err}). Please try again.`
        : 'Something went wrong. Please try again.';
  }
}
